import { clsx } from 'clsx';
import { NavLink } from 'react-router-dom';

interface TabItem {
  /** Link URL. */
  to: string;

  /** Tab label text. */
  label: string;

  /** If `true`, the link will be an exact match. */
  end?: boolean;

  /** Condition to show/hide the tab. Defaults to `true`. */
  condition?: boolean;
}

interface TabbedPageHeaderProps {
  /** Main title of the page. */
  title: string;

  /** Optional subtitle displayed below the title. */
  subtitle?: string;

  /** Array of tab navigation items. */
  tabs: TabItem[];

  /** Optional breadcrumb element displayed above the title. */
  breadcrumb?: React.ReactNode;

  /** Custom class name for the component wrapper. */
  className?: string;
}

function TabbedPageHeader({
  title,
  subtitle,
  tabs,
  breadcrumb,
  className
}: TabbedPageHeaderProps) {
  const visibleTabs = tabs.filter(tab => tab.condition !== false);

  return (
    <div className={clsx('w-full', className)}>
      {breadcrumb && (
        <div className="w-full mt-4 p-4 bg-[#cbe3ed] rounded mb-2">
          {breadcrumb}
        </div>
      )}

      <h1 className="text-4xl text-black">{title}</h1>

      {subtitle && <p className="mt-2 text-lg text-gray-600">{subtitle}</p>}

      <div className="mt-10">
        <div className="inline-flex border-b-2 border-[#E0E0E0]">
          {visibleTabs.map((tab, index) => (
            <NavLink
              key={`${tab.to}-${index}`}
              end={tab.end}
              to={tab.to}
              className={({ isActive }) =>
                clsx(
                  'text-black inline-block py-2 px-1 sm:px-0 transition-colors duration-150 sm:text-center',
                  {
                    [`font-bold border-b-4 border-[#61810D]`]: isActive,
                    isActive,
                    'ml-2': index > 0,
                    'border-b-4 border-transparent': !isActive
                  }
                )
              }
            >
              <span className="md:mx-4 sm:mx-0">{tab.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TabbedPageHeader;
